import React, { useEffect, useState } from "react";
import CommonLayout from "../CommonLayout";
import Mobile_Number_Header from "../../../Assets/mobile_number.png";

import {
  Button,
  Form,
  Input,
  message,
  Typography,
} from "antd";

import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { LockOutlined } from "@ant-design/icons";

import "./index.css";

import {
  getMobileIdentifiers,
  deleteIdentifier,
  verifyAndDeleteIdentifier,
  addIdentifier,
  addIdentifierVerification,
} from "../../../Services/IdentifierService/Identifier";

import { translation } from "../../../i18n/translationConstant";
import OTPInputComponent from "../../Common/Components/OTPModal";

import { AddIdentifier } from "../../../Models/Request/Identifier/AddIdentifier";
import { IdentifierCategory } from "../../../Utils/Constants/identifierType";
import { verifyFingerPrint } from "../../../Services/AuthenticationServices/AuthenticationServices";
import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

interface MobileIdentifier {
  id: string;
  type: string;
  value: string;
  categoryType: string;
  primary: boolean;
}

const MobileNumbers: React.FC = () => {
  const [form] = useForm();
  const { t } = useTranslation();

  const [mobileIdentifiers, setMobileIdentifiers] = useState<
    MobileIdentifier[]
  >([]);

  const [loading, setLoading] = useState(false);

  // ============================================================
  // DELETE OTP STATES
  // ============================================================

  const [isDeleteOTPModalVisible, setIsDeleteOTPModalVisible] =
    useState(false);

  const [deleteOtpUniqueID, setDeleteOtpUniqueID] =
    useState("");

  const [selectedIdentifierId, setSelectedIdentifierId] =
    useState("");

  const [selectedDeleteMobileNumber, setSelectedDeleteMobileNumber] =
    useState("");

  const [selectedDeleteIdentifier, setSelectedDeleteIdentifier] =
    useState<MobileIdentifier | null>(null);

  // ============================================================
  // ADD OTP STATES
  // ============================================================

  const [isAddOTPModalVisible, setIsAddOTPModalVisible] =
    useState(false);

  const [addOtpUniqueID, setAddOtpUniqueID] =
    useState("");

  const [selectedAddIdentifier, setSelectedAddIdentifier] =
    useState<AddIdentifier | null>(null);

  // ============================================================
  // LOAD MOBILE NUMBERS
  // ============================================================

  const {checkFingerprint} = useFingerprintValidation();

  useEffect(() => {
    getMobileIdentifiersFiltered();

    // Check fingerprint
    checkFingerprint();
  }, [checkFingerprint]);

  const getMobileIdentifiersFiltered = () => {
    setLoading(true);

    getMobileIdentifiers()
      .then((res) => res.data)
      .then((res) => {
        setMobileIdentifiers(res || []);
      })
      .catch((error) => {
        console.error(
          "Get mobile identifiers error:",
          error
        );

        message.error(
          "Unable to load mobile numbers"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ============================================================
  // PRIMARY / SECONDARY
  // ============================================================

  const primaryNumber = mobileIdentifiers.find(
    (identifier) => identifier.primary
  );

  const secondaryNumbers = mobileIdentifiers.filter(
    (identifier) => !identifier.primary
  );

  // ============================================================
  // ADD SECONDARY MOBILE NUMBER
  // ============================================================

  const formSubmit = (values: any) => {
    const mobileNumber = values.secondaryNumber;

    if (!mobileNumber) {
      message.error(
        "Please enter mobile number"
      );
      return;
    }

    const identifier: AddIdentifier = {
      type: "MOBILE",
      categoryType: IdentifierCategory.STRONG,
      value: mobileNumber,
    };

    setLoading(true);

    addIdentifier(identifier)
      .then((res) => res.data)
      .then((res) => {
        console.log(
          "Add identifier response:",
          res
        );

        setSelectedAddIdentifier(identifier);

        setAddOtpUniqueID(
          res.mobile.otpUniqueID
        );

        setIsAddOTPModalVisible(true);
      })
      .catch((error) => {
        console.error(
          "Add identifier error:",
          error
        );

        message.error(
          error?.response?.data?.message ||
            "Unable to add mobile number"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ============================================================
  // VERIFY ADD MOBILE NUMBER
  // ============================================================

  const verifyAddMobileNumber = (
    otp: string
  ) => {
    if (!selectedAddIdentifier) {
      return;
    }

    addIdentifierVerification({
      code: otp,
      otpUniqueID: addOtpUniqueID,
      identifierValue:
        selectedAddIdentifier.value,
      categoryType:
        selectedAddIdentifier.categoryType,
      type: selectedAddIdentifier.type,
    })
      .then(() => {
        message.success(
          "Mobile number added successfully"
        );

        setIsAddOTPModalVisible(false);

        setAddOtpUniqueID("");

        setSelectedAddIdentifier(null);

        form.resetFields([
          "secondaryNumber",
        ]);

        getMobileIdentifiersFiltered();
      })
      .catch((error) => {
        console.error(
          "Verify add identifier error:",
          error
        );

        message.error(
          error?.response?.data?.message ||
            "Invalid OTP"
        );
      });
  };

  // ============================================================
  // RESEND ADD OTP
  // ============================================================

  const resendAddOTP = () => {
    if (!selectedAddIdentifier) {
      return;
    }

    addIdentifier(selectedAddIdentifier)
      .then((res) => res.data)
      .then((res) => {
        setAddOtpUniqueID(
          res.mobile.otpUniqueID
        );

        message.success(
          "OTP sent successfully"
        );
      })
      .catch((error) => {
        console.error(
          "Resend add OTP error:",
          error
        );

        message.error(
          error?.response?.data?.message ||
            "Unable to resend OTP"
        );
      });
  };

  // ============================================================
  // CLOSE ADD OTP
  // ============================================================

  const closeAddOTPModal = () => {
    setIsAddOTPModalVisible(false);

    setAddOtpUniqueID("");

    setSelectedAddIdentifier(null);
  };

  // ============================================================
  // DELETE MOBILE NUMBER
  // ============================================================

  const deleteMobileNumber = (
    identifier: MobileIdentifier
  ) => {
    setSelectedDeleteIdentifier(
      identifier
    );

    setSelectedIdentifierId(
      identifier.id
    );

    setSelectedDeleteMobileNumber(
      identifier.value
    );

    deleteIdentifier(identifier.id)
      .then((res) => res.data)
      .then((res) => {
        console.log(
          "Delete identifier response:",
          res
        );

        setDeleteOtpUniqueID(
          res.mobile.otpUniqueID
        );

        setIsDeleteOTPModalVisible(true);
      })
      .catch((error) => {
        console.error(
          "Delete identifier error:",
          error
        );

        // message.error(
        //   error?.response?.data?.message ||
        //     "Unable to delete mobile number"
        // );

        setSelectedDeleteIdentifier(null);
        setSelectedIdentifierId("");
        setSelectedDeleteMobileNumber("");
      });
  };

  // ============================================================
  // VERIFY DELETE MOBILE NUMBER
  // ============================================================

  const verifyDeleteMobileNumber = (
    otp: string
  ) => {
    if (!selectedIdentifierId) {
      return;
    }

    verifyAndDeleteIdentifier({
      id: selectedIdentifierId,
      otpUniqueID: deleteOtpUniqueID,
      code: otp,
    })
      .then(() => {
        message.success(
          "Mobile number deleted successfully"
        );

        setIsDeleteOTPModalVisible(false);

        setSelectedDeleteIdentifier(null);
        setSelectedIdentifierId("");
        setSelectedDeleteMobileNumber("");
        setDeleteOtpUniqueID("");

        getMobileIdentifiersFiltered();
      })
      .catch((error) => {
        console.error(
          "Verify delete identifier error:",
          error
        );

        message.error(
          error?.response?.data?.message ||
            "Invalid OTP"
        );
      });
  };

  // ============================================================
  // RESEND DELETE OTP
  // ============================================================

  const resendDeleteOTP = () => {
    if (!selectedDeleteIdentifier) {
      return;
    }

    deleteIdentifier(
      selectedDeleteIdentifier.id
    )
      .then((res) => res.data)
      .then((res) => {
        setDeleteOtpUniqueID(
          res.mobile.otpUniqueID
        );

        message.success(
          "OTP sent successfully"
        );
      })
      .catch((error) => {
        console.error(
          "Resend delete OTP error:",
          error
        );

        message.error(
          error?.response?.data?.message ||
            "Unable to resend OTP"
        );
      });
  };

  // ============================================================
  // CLOSE DELETE OTP
  // ============================================================

  const closeDeleteOTPModal = () => {
    setIsDeleteOTPModalVisible(false);

    setSelectedDeleteIdentifier(null);
    setSelectedIdentifierId("");
    setSelectedDeleteMobileNumber("");
    setDeleteOtpUniqueID("");
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ========================================================
          ADD MOBILE OTP
          ======================================================== */}

      <OTPInputComponent
        title={t(translation.lblVerify)}
        isOpen={isAddOTPModalVisible}
        onOtpSubmit={verifyAddMobileNumber}
        onCancel={closeAddOTPModal}
        okText={t(translation.lblConfirm)}
        onResend={resendAddOTP}
        mobileNumber={
          selectedAddIdentifier?.value || ""
        }
      />

      {/* ========================================================
          DELETE MOBILE OTP
          ======================================================== */}

      <OTPInputComponent
        title={t(translation.lblVerify)}
        isOpen={isDeleteOTPModalVisible}
        onOtpSubmit={verifyDeleteMobileNumber}
        onCancel={closeDeleteOTPModal}
        okText={t(translation.lblConfirm)}
        onResend={resendDeleteOTP}
        mobileNumber={
          selectedDeleteMobileNumber
        }
      />

      {/* ========================================================
          MOBILE NUMBER PAGE
          ======================================================== */}

      <CommonLayout
        imageUrl={Mobile_Number_Header}
        title={t(translation.lblMobileNumbers)}
      >
        <div className="mobileNumbersPage">

          <div className="mobileNumbersFormContainer">

            <Form
              form={form}
              name="mobileNumberForm"
              onFinish={formSubmit}
              autoComplete="off"
              className="mobileNumbersForm"
            >

              {/* =================================================
                  PRIMARY MOBILE NUMBER
                  ================================================= */}

              <div className="mobilePrimarySection">

                <Typography.Text className="mobileFieldLabel">
                  {t(
                    "lblPrimaryMobileNumbers"
                  )}
                </Typography.Text>

                {primaryNumber && (
                  <Form.Item
                    name="primaryNumber"
                    initialValue={
                      primaryNumber.value
                    }
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    <div className="mobilePrimaryBox">

                      <div className="mobilePrimaryNumber">

                        <span className="mobileCountryCode">
                          +91
                        </span>

                        <span className="mobileMaskedNumber">
                          {
                            primaryNumber.value
                          }
                        </span>

                      </div>

                      <div className="mobilePrimaryBadge">

                        <LockOutlined
                          className="mobileLockIcon"
                        />

                        <span>
                          Primary
                        </span>

                      </div>

                    </div>
                  </Form.Item>
                )}

                <Typography.Text className="mobileDescription">
                  {t(
                    "lblPrimaryMobileNumbersDesc"
                  )}
                </Typography.Text>

              </div>

              {/* =================================================
                  SECONDARY MOBILE NUMBERS
                  ================================================= */}

              <div className="mobileSecondarySection">

                <div className="mobileSecondaryLabel">

                  <span className="mobileSecondaryTitle">
                    {t(
                      "lblSecondaryMobileNumbers"
                    )}
                  </span>

                  <span className="mobileSecondaryOptional">
                    (Optional)
                  </span>

                </div>

                {/* =================================================
                    EXISTING SECONDARY NUMBERS
                    ================================================= */}

                {secondaryNumbers.length > 0 && (
                  <div className="mobileSecondaryNumbersBox">

                    {secondaryNumbers.map(
                      (
                        identifier,
                        index
                      ) => (
                        <div
                          className="mobileSecondaryNumberRow"
                          key={`${identifier.id}-${index}`}
                        >

                          <div className="mobileSecondaryNumberValue">

                            <span className="mobileCountryCode">
                              +91
                            </span>

                            <span className="mobileSecondaryNumberText">
                              {
                                identifier.value
                              }
                            </span>

                          </div>

                          <Button
                            type="link"
                            danger
                            className="mobileEditButton"
                            onClick={() =>
                              deleteMobileNumber(
                                identifier
                              )
                            }
                          >
                            Delete
                          </Button>

                        </div>
                      )
                    )}

                  </div>
                )}

                {/* =================================================
                    ADD SECONDARY NUMBER

                    Only show when there is currently
                    one mobile number.
                    ================================================= */}

                {mobileIdentifiers.length <= 1 && (
                  <Form.Item
                    name="secondaryNumber"
                    style={{
                      marginBottom: 0,
                      marginTop:
                        secondaryNumbers.length >
                        0
                          ? "10px"
                          : "8px",
                    }}
                    rules={[
                      {
                        pattern:
                          /^[0-9]{10}$/,
                        message:
                          "Please enter a valid 10 digit mobile number",
                      },
                    ]}
                  >

                    <Input
                      className="mobileNumberInput"
                      placeholder="Enter mobile number"
                      maxLength={10}
                      inputMode="numeric"
                      prefix={
                        <span className="mobileInputPrefix">

                          <span>
                            +91
                          </span>

                          <span className="mobilePrefixDivider" />

                        </span>
                      }
                    />

                  </Form.Item>
                )}

                <Typography.Text className="mobileDescription">
                  {t(
                    "lblSecondaryMobileNumbersDesc"
                  )}
                </Typography.Text>

              </div>

              {/* =================================================
                  UPDATE BUTTON
                  ================================================= */}

              {mobileIdentifiers.length <= 1 && (
                <div className="mobileSaveButtonWrapper">

                  <Button
                    htmlType="submit"
                    className="profileSubmitButton"
                    loading={loading}
                  >
                    {t(
                      "lblUpdateDetails"
                    )}
                  </Button>

                </div>
              )}

            </Form>

          </div>

        </div>
      </CommonLayout>
    </>
  );
};

export default MobileNumbers;